import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

export interface StcResourceDto {
    id: number;
    companyNo: string;
    resourceNo: number;
    description1: string;
    updatedBy?: string;
    lastUpdate?: string;
    createdBy?: string;
    created?: string;
}

export interface CreateStcResourceRequest {
    companyNo: string;
    resourceNo: number;
    description1: string;
}

export interface UpdateStcResourceRequest {
    description1: string;
}

export interface BulkUpdateStcResourceRequest {
    companyNo: string;
    resourceNo: number;
    description1: string;
}

export interface BulkDeleteResult {
    deletedCount: number;
    requestedCount: number;
    notFoundIds: number[];
}

class StcResourceService {
    private baseUrl = `${API_BASE_URL}/stc-resources`;

    /**
     * Get resource by company and resource number
     */
    async getByCompanyAndResourceNo(companyNo: string, resourceNo: number): Promise<StcResourceDto | null> {
        try {
            const response = await AuthService.authenticatedGet(`${this.baseUrl}/${companyNo}/${resourceNo}`);
            if (!response.ok) {
                if (response.status === 404) {
                    return null;
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return await response.json();
        } catch (error: any) {
            if (error.message?.includes('404')) {
                return null;
            }
            throw error;
        }
    }

    /**
     * Get all resources for a company
     */
    async getByCompanyNo(companyNo: string): Promise<StcResourceDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/company/${companyNo}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    /**
     * Get all resources for current tenant
     */
    async getCurrent(): Promise<StcResourceDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/current`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    /**
     * Get all resources
     */
    async getAll(): Promise<StcResourceDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    /**
     * Create new resource
     */
    async create(request: CreateStcResourceRequest): Promise<StcResourceDto> {
        const response = await AuthService.authenticatedPost(this.baseUrl, request);
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    /**
     * Update resource
     */
    async update(companyNo: string, resourceNo: number, request: UpdateStcResourceRequest): Promise<StcResourceDto> {
        const response = await AuthService.authenticatedPut(`${this.baseUrl}/${companyNo}/${resourceNo}`, request);
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    /**
     * Delete resource
     */
    async delete(companyNo: string, resourceNo: number): Promise<boolean> {
        const response = await AuthService.authenticatedDelete(`${this.baseUrl}/${companyNo}/${resourceNo}`);
        if (!response.ok) {
            if (response.status === 404) {
                return false;
            }
            const error = await response.json();
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return true;
    }

    /**
     * Check if resource exists
     */
    async exists(companyNo: string, resourceNo: number): Promise<boolean> {
        try {
            const response = await AuthService.authenticatedFetch(`${this.baseUrl}/${companyNo}/${resourceNo}`, {
                method: 'HEAD'
            });
            return response.ok;
        } catch (error) {
            return false;
        }
    }

    /**
     * Search resources
     */
    async search(companyNo: string, searchTerm: string): Promise<StcResourceDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/search/${companyNo}?searchTerm=${encodeURIComponent(searchTerm)}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    /**
     * Bulk update resources
     */
    async bulkUpdate(requests: BulkUpdateStcResourceRequest[]): Promise<StcResourceDto[]> {
        const response = await AuthService.authenticatedPut(`${this.baseUrl}/bulk`, requests);
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    /**
     * Bulk delete resources by IDs
     */
    async bulkDelete(ids: number[]): Promise<BulkDeleteResult> {
        const response = await AuthService.authenticatedDelete(`${this.baseUrl}/bulk`, ids);
        if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }
}

export const stcResourceService = new StcResourceService();
